import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import Media from 'react-media';
import loadable from '@loadable/component';

import { Modal, useTheme } from '@simplywallst/ui-core';
import { Button } from '@simplywallst/ui-core/next';
import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';
import { push } from 'redux-first-router';
import { getPath } from '@/router/utils';
import { ROUTE_PUBLIC_NARRATIVE_CREATE } from '@/constants/routes';
import { usePathname } from '@/hooks/location/usePathname';
import { getCompanyIdByUniqueSymbol } from './utils';
import { useSnackbar } from '@/features/snackbar';
import { useTracking } from 'react-tracking';
import { useCreateNarrativeLink } from '../../hooks/useCreateNarrativeLink';

const AlgoliaSearch = loadable(
  () => import('@/components/Search/components/AlgoliaSearch')
);
const BasicSearch = loadable(
  () => import('@/components/Search/components/BasicSearch'),
  {
    fallback: <div className="bones h-9 w-[146px]" />,
  }
);

type Props = {
  companyId: string | undefined | null;
};

export const NarrativeCreateButton = ({ companyId }: Props) => {
  const theme = useTheme();
  const [showSearch, setShowSearch] = useState(false);
  const pathname = usePathname();
  const { addSnackbar } = useSnackbar();
  const { trackEvent } = useTracking();
  const { href, onClick } = useCreateNarrativeLink({
    companyId,
  });

  const handleClick = useCallback(() => {
    trackEvent({
      action: 'click',
      subject: 'create_narrative_button',
    });
    setShowSearch((prevState) => !prevState);
  }, [trackEvent]);

  const handleSelect = useCallback(
    async ({
      uniqueSymbol,
      companyId,
    }: {
      uniqueSymbol: string;
      companyId?: string;
    }) => {
      // Secondary listing selection doesn't have a companyId
      // Providing temporary fallback to resolve the company id by unique symbol
      // Until Josh adds the companyId to the search results
      // https://simplywallst.slack.com/archives/C02BYCPRT6C/p1726031424815839?thread_ts=1726010409.805699&cid=C02BYCPRT6C
      const targetCompanyId =
        companyId || (await getCompanyIdByUniqueSymbol(uniqueSymbol));

      if (!targetCompanyId) {
        // This should only happen on DEV where we might be missing some companies
        addSnackbar({
          type: 'negative',
          message: 'Unable to create a narrative for this company',
          lifeTime: 15_000,
        });
        setShowSearch(false);
        return;
      }

      push(
        getPath({
          type: ROUTE_PUBLIC_NARRATIVE_CREATE,
          payload: {
            query: { company: targetCompanyId, returnPath: pathname },
          },
        })
      );

      setShowSearch(false);
    },
    [pathname, addSnackbar]
  );

  const getResult = useCallback(
    (
      child: ReactNode,
      result: {
        id?: string;
        uniqueSymbol: string;
      }
    ) => (
      <div
        className="cursor-pointer"
        onClick={() => {
          handleSelect({
            uniqueSymbol: result.uniqueSymbol,
            companyId: result.id,
          });
        }}
      >
        {child}
      </div>
    ),
    [handleSelect]
  );

  if (companyId && href)
    return (
      <Button variant="primary" size="medium" href={href} onClick={onClick}>
        <PlusSolid />
        Create a narrative
      </Button>
    );

  return (
    <section className="grid min-h-9 items-center">
      {showSearch ? (
        <Media query={{ maxWidth: theme.mdMaxRaw }}>
          {(match) => {
            if (match) {
              return (
                <Modal isNaked onSecondaryClick={handleClick}>
                  <AlgoliaSearch
                    placeholder="Select a company"
                    onClose={handleClick}
                    primaryWrapper={getResult}
                    secondaryWrapper={getResult}
                  />
                </Modal>
              );
            }
            return (
              <BasicSearch
                size="medium"
                placeholder="Select a company"
                onClick={(uniqueSymbol, { companyId }) =>
                  handleSelect({ uniqueSymbol, companyId })
                }
              />
            );
          }}
        </Media>
      ) : (
        <Button variant="primary" size="medium" onClick={handleClick}>
          <PlusSolid />
          Create a narrative
        </Button>
      )}
    </section>
  );
};
